// PO7-S5 (docs/build-briefs/PO7-S5-invoice-scan-receive.md). Camera pages become one PDF in the browser, so a
// multi-page invoice goes through the existing PO7-S1 attach path as one file and the PO7-S2 worker reads it whole.
// No library and nothing loaded at runtime: a PDF whose pages each hold one JPEG is a short, fixed structure, and
// PDF's DCTDecode filter takes the camera's JPEG bytes unchanged (nothing is re-encoded here).
//
// Pure: bytes in, bytes out, no DOM, so test/ui-receive-scan-pdf.test.ts proves the structure (header, page count,
// one image stream per page, every cross-reference offset pointing at its object) without a browser.

export interface PdfPage {
  /** A baseline JPEG, three components (what canvas.toBlob("image/jpeg") produces). */
  jpeg: Uint8Array;
  width: number;
  height: number;
}

const ENCODER = new TextEncoder();

function ascii(text: string): Uint8Array {
  return ENCODER.encode(text);
}

function concat(chunks: readonly Uint8Array[]): Uint8Array {
  let total = 0;
  for (const chunk of chunks) {
    total += chunk.length;
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

function pad10(value: number): string {
  return String(value).padStart(10, "0");
}

/** One PDF, one page per image, in order. Throws on an empty list or a page with no JPEG or a bad size. */
export function buildJpegPdf(pages: readonly PdfPage[]): Uint8Array {
  if (pages.length === 0) {
    throw new Error("A PDF needs at least one page.");
  }
  for (const page of pages) {
    if (
      page.jpeg.length < 4 ||
      page.jpeg[0] !== 0xff ||
      page.jpeg[1] !== 0xd8 ||
      !Number.isInteger(page.width) ||
      !Number.isInteger(page.height) ||
      page.width <= 0 ||
      page.height <= 0
    ) {
      throw new Error("Every page needs a JPEG and its size.");
    }
  }

  // Object numbers: 1 catalog, 2 page tree, then three per page (page, contents, image).
  const pageObject = (index: number): number => 3 + index * 3;
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let position = 0;
  const push = (chunk: Uint8Array): void => {
    chunks.push(chunk);
    position += chunk.length;
  };
  const startObject = (number: number): void => {
    offsets[number] = position;
  };

  // The binary comment tells a transfer tool this file holds binary data.
  push(concat([ascii("%PDF-1.4\n%"), Uint8Array.from([0xe2, 0xe3, 0xcf, 0xd3]), ascii("\n")]));

  startObject(1);
  push(ascii("1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n"));

  const kids = pages.map((_, index) => `${pageObject(index)} 0 R`).join(" ");
  startObject(2);
  push(ascii(`2 0 obj\n<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>\nendobj\n`));

  pages.forEach((page, index) => {
    const pageNumber = pageObject(index);
    const contentsNumber = pageNumber + 1;
    const imageNumber = pageNumber + 2;
    startObject(pageNumber);
    push(
      ascii(
        `${pageNumber} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${page.width} ${page.height}] ` +
          `/Resources << /XObject << /Im0 ${imageNumber} 0 R >> >> /Contents ${contentsNumber} 0 R >>\nendobj\n`,
      ),
    );
    const drawing = ascii(`q ${page.width} 0 0 ${page.height} 0 0 cm /Im0 Do Q\n`);
    startObject(contentsNumber);
    push(ascii(`${contentsNumber} 0 obj\n<< /Length ${drawing.length} >>\nstream\n`));
    push(drawing);
    push(ascii("endstream\nendobj\n"));
    startObject(imageNumber);
    push(
      ascii(
        `${imageNumber} 0 obj\n<< /Type /XObject /Subtype /Image /Width ${page.width} /Height ${page.height} ` +
          `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.jpeg.length} >>\nstream\n`,
      ),
    );
    push(page.jpeg);
    push(ascii("\nendstream\nendobj\n"));
  });

  const objectCount = 2 + pages.length * 3;
  const xrefStart = position;
  const lines = [`xref\n0 ${objectCount + 1}\n`, "0000000000 65535 f \n"];
  for (let number = 1; number <= objectCount; number += 1) {
    lines.push(`${pad10(offsets[number] ?? 0)} 00000 n \n`);
  }
  lines.push(
    `trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`,
  );
  push(ascii(lines.join("")));
  return concat(chunks);
}
